import { Component, Input, Renderer2 } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { ThesaurusEntry } from "@interfaces/thesaurus-entry.interface";
import { TermsService } from "@terms-service/terms.service";
import { ThesaurusService } from '@thesaurus-service/thesaurus.service';
import { EntryService } from "app/services/entry/entry.service";
import { Subscription, combineLatest} from "rxjs";
import { UtilitiesService } from "@utilities-service/utilities.service";
import { EventsService } from "app/services/events/events.service";
import { NgxSpinnerService } from "ngx-spinner";

@Component({
    selector: 'app-thesaurus-entry',
    templateUrl: './thesaurus-entry.component.html',
    styleUrl: './thesaurus-entry.component.scss',
    standalone: false
})
export class ThesaurusEntryComponent {
    @Input() entry!: ThesaurusEntry;
    @Input() index!: string;
    @Input() explode!: boolean;
    @Input() major!: boolean;
    @Input() notesId!: string;
    @Input() collapsingNotesId!: string;
    @Input() linkable: boolean = true;
    @Input() isRoot: boolean = false;
    private termsSubscription!: Subscription;
    majorType: string = 'major';
    explodeType: string = 'explode';
    mainTerm: string = 'main';
    showClass: string = "show";
    collapsingClass: string = 'collapsing';
    collapseClass: string = 'collapse';
    subscription: any;
    thesaurusId: string = '';
    pathIdentifier: string = '';

    constructor(
      private thesaurusService: ThesaurusService,
      private route: ActivatedRoute,
      public termsService: TermsService,
      private renderer: Renderer2,
      private entryService: EntryService,
      public utilitiesService: UtilitiesService, 
      private eventsService: EventsService, 
      private spinnerService: NgxSpinnerService
    ) {}

    ngOnInit(): void {
      this.subscription = combineLatest([this.route.params, this.route.queryParams])
            .subscribe(([params, queryParams]) => {
              this.thesaurusId = params['thesaurusId'];
            });
      this.pathIdentifier = (this.entry.paths?.[0] ? this.utilitiesService.getPathIdentifier(this.entry.paths) : "");
            //node.termOrdinal + "-" + (node.paths?.[0] ? this.utilitiesService.getPathIdentifier(node.paths) : "")
        this.eventsService.getShowEntrySpinner().subscribe((entryId: string) => {
          if(entryId == (this.entry.termOrdinal + this.pathIdentifier)) {
            this.showSpinner();
          }
        });
        this.eventsService.getHideEntrySpinner().subscribe((entryId: string) => {
          if(entryId == (this.entry.termOrdinal + this.pathIdentifier)) {
            this.hideSpinner();
          }
        });
   }

   ngOnDestroy() {
    this.subscription.unsubscribe();
   }

   showFrillsLexicon(entry: any) {
      if(entry.name === undefined || entry.name === null || entry.name === '') {
        return false;
      }
      return (entry?.useTermsInstead ?? []).length === 0 && !this.utilitiesService.getLookupQueryParam();
    }

    showBusinessThesaurus(entry: any) {
      if(entry.name === undefined || entry.name === null || entry.name === '') {
        return false;
      }
      return this.thesaurusService.isBusinessThesaurus(entry.thesaurusId) || this.thesaurusService.isCorporationThesaurus(entry.thesaurusId);
    }

   onEntryCheckboxChange(entry: ThesaurusEntry, event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;
    if (!entry.paths || entry.paths.length === 0) {
      this.termsService.updateList(entry.name, this.mainTerm, isChecked, this.entry.notUnique, entry.paths);

      //When the main checkbox is unchecked, uncheck the Explode and Major checkboxes
      if (!isChecked) {
        this.termsService.updateCheckedState(entry.name, this.explodeType, isChecked, entry.paths);
        this.termsService.updateCheckedState(entry.name, this.majorType, isChecked, entry.paths);
      }
    } else {
      const hasMultiplePaths = (entry.paths ?? []).length > 1;
      entry.paths.forEach((path) => {
        this.termsService.updateList(entry.name, this.mainTerm, isChecked, this.entry.notUnique || hasMultiplePaths, [path]);
        
        //When the main checkbox is unchecked, uncheck the Explode and Major checkboxes
        if (!isChecked) {
          this.termsService.updateCheckedState(entry.name, this.explodeType, isChecked, [path]);
          this.termsService.updateCheckedState(entry.name, this.majorType, isChecked, [path]);
        }
      });
    }    
  }

  onEntryExplodeCheckboxChange(entry: ThesaurusEntry, event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;

    //When the Explode checkbox is selected but the "main" checkbox is unchecked, set the "checked" state of the "main" checkbox to true
    if (!entry.paths || entry.paths.length === 0) {
      if (!this.termsService.isChecked(entry.name, entry.paths) && isChecked) {
        this.termsService.updateList(entry.name, this.mainTerm, isChecked, this.entry.notUnique, entry.paths);
      }

      this.termsService.updateCheckedState(entry.name, this.explodeType, isChecked, entry.paths);
    } else {
      const hasMultiplePaths = (entry.paths ?? []).length > 1;
      entry.paths.forEach((path) => {
        if (!this.termsService.isChecked(entry.name, [path]) && isChecked) {
          this.termsService.updateList(entry.name, this.mainTerm, isChecked, this.entry.notUnique || hasMultiplePaths, [path]);
        }

        this.termsService.updateCheckedState(entry.name, this.explodeType, isChecked, [path]);
      });
    }
  }

  onEntryMajorCheckboxChange(entry: ThesaurusEntry, event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;

    //When the Major checkbox is selected but the "main" checkbox is unchecked, set the "checked" state of the "main" checkbox to true
    if (!entry.paths || entry.paths.length === 0) {
      if (!this.termsService.isChecked(entry.name, entry.paths) && isChecked) {
        this.termsService.updateList(entry.name, this.mainTerm, isChecked, this.entry.notUnique, entry.paths);
      }
      this.termsService.updateCheckedState(entry.name, this.majorType, isChecked, entry.paths);
    } else {
      const hasMultiplePaths = (entry.paths ?? []).length > 1;
      entry.paths.forEach((path) => {
        if (!this.termsService.isChecked(entry.name, [path]) && isChecked) {
          this.termsService.updateList(entry.name, this.mainTerm, isChecked, this.entry.notUnique || hasMultiplePaths, [path]);
        }
        this.termsService.updateCheckedState(entry.name, this.majorType, isChecked, [path]);
      });
    }
  }

  onEntryClick(ordinal: number): void {
    this.entryService.emitEntryClick(this.thesaurusId, ordinal);
  }

  //Toggle for collapsing-notes and collapsing-qualifier-notes components. (The toggle functionality for the Bootstrap collapse component did not work when implemented--the collapsing animation gets interrupted when trying to toggle an element to close--so this is a workaround.)
  toggleCollapse(collapseId: string): void {
    const collapseElement = document.getElementById(collapseId);
    const triggerElement = document.querySelector(`[aria-controls="${collapseId}"]`);

    const allCollapseElements = [...Array.from(document.querySelectorAll('.show')), ...Array.from(document.querySelectorAll('.bi-chevron-double-down.hidden')).map(e => e.parentElement)];
console.log('allCollapseElements:', allCollapseElements);
console.log('collapseElement:', collapseElement);
console.log('triggerElement:', triggerElement);
    if (collapseElement && triggerElement) {
      const isCollapsing = collapseElement.classList.contains(this.showClass);

      // Close all other open collapse elements
      this.closeOpenCollapseElements(allCollapseElements, collapseId);

      if (isCollapsing) {
        this.renderer.removeClass(collapseElement, this.showClass);
        this.renderer.addClass(collapseElement, this.collapsingClass);
        setTimeout(() => {
          this.renderer.removeClass(collapseElement, this.collapsingClass);
          this.renderer.addClass(collapseElement, this.collapseClass);
          this.renderer.setAttribute(triggerElement, 'aria-expanded', 'false');
        }, 10);
      } else {
        this.renderer.removeClass(collapseElement, this.collapseClass);
        this.renderer.addClass(collapseElement, this.collapsingClass);
        this.renderer.setAttribute(triggerElement, 'aria-expanded', 'true');
        setTimeout(() => {
          this.renderer.removeClass(collapseElement, this.collapsingClass);
          this.renderer.addClass(collapseElement, this.showClass);
        }, 10);
      }
    }
  }

  //When a collapsing-note is opened, close any other collapsing-note that might be open.
  closeOpenCollapseElements(allCollapseElements: Array<Element | null>, collapseId: string) {
    allCollapseElements.forEach((element) => {
      const htmlElement = element as HTMLElement | null;
      if (!htmlElement || htmlElement.id === collapseId) return;

      if (htmlElement.id !== collapseId) {
        this.renderer.removeClass(htmlElement, this.showClass);
        this.renderer.addClass(htmlElement, this.collapsingClass);

        const qualifierTrigger = htmlElement instanceof HTMLAnchorElement && htmlElement.getAttribute('href')?.startsWith('#qualDescr');

        if (qualifierTrigger) {
          const href = htmlElement.getAttribute('href');
          const match = href?.match(/#qualDescr-(.*)/);
          const suffix = match ? match[1] : "";

          const downChevron = document.getElementById(`chevron-double-down-${suffix}`);
          const upChevron = document.getElementById(`chevron-double-up-${suffix}`);
          
          this.renderer.setAttribute(htmlElement, 'aria-expanded', 'false');
          this.renderer.removeClass(downChevron, "hidden");
          this.renderer.addClass(upChevron, "hidden");
        } else {
          setTimeout(() => {
            this.renderer.removeClass(htmlElement, this.collapsingClass);
            this.renderer.addClass(htmlElement, this.collapseClass);
          }, 10);
        }
      }
    });
  }

  //Collapse open qualifier descriptions and reset chevron icons
  // collapseQualifiers(element: HTMLElement) {
  //   const href = element.getAttribute('href');
  //   const match = href?.match(/#qualDescr-(\d+)/);
  //   const index = match ? parseInt(match[1], 10) : null;

  //   const downChevron = document.getElementById(`chevron-double-down-${index}`);
  //   const upChevron = document.getElementById(`chevron-double-up-${index}`);
    
  //   this.renderer.setAttribute(element, 'aria-expanded', 'false');
  //   this.renderer.removeClass(downChevron, "hidden");
  //   this.renderer.addClass(upChevron, "hidden");
  // }

  //To supress the display of icons next to term names in the Browse view. Applies to the following thesauri only: Lexicon, Corporation, Business Subject.
  isHideIcon(): boolean {
    if (this.thesaurusId) {
      return this.thesaurusService.isLexiconThesaurus(this.thesaurusId) || this.thesaurusService.isCorporationThesaurus(this.thesaurusId) || this.thesaurusService.isBusinessThesaurus(this.thesaurusId);
    }
    return false;
  }

  showCheckbox(): boolean {
    if (this.thesaurusId) {
      if(this.thesaurusService.isThesaurusWithRoot(this.thesaurusId) && this.isRoot ) {
        return false;
      }
      return ((this.entry?.useTermsInstead ?? []).length === 0);
    }
    return false;
  }

  //To check if the any of the following properties are available for a given entry: useTermFor, description, relatedTerms. Used in the "no icon" version of the collapsing-notes component, which appears in the Lexicon, Corporation, and Business thesauri.
  isDetailsAvailable(): boolean {
    return !!(this.entry?.useTermFor || this.entry?.description || this.entry?.relatedTerms);
  }

  isUseTermForAvailable(): boolean {
    return !!this.entry?.useTermFor && this.entry.useTermFor.length > 0;
  }

  isNarrowerTermsAvailable(): boolean {
    return !!this.entry?.narrowerTerms && this.entry.narrowerTerms.length > 0;
  }

  isBroaderTermsAvailable(): boolean {
    return !!this.entry?.broaderTerms && this.entry.broaderTerms.length > 0;
  }

  isRelatedTermsAvailable(): boolean {
    return !!this.entry?.relatedTerms && this.entry.relatedTerms.length > 0;
  }

  //To check if any qualifiers exist for a given entry. Used for the collapsing-qualifier-notes component, which appears in MeSH.
  hasQualifiers(): boolean {
    let arrLength: number = this.entry.qualifiers?.length ?? 0;
    return arrLength > 0;
  }

   hideSpinnerContainer() {
     let spinnerElement = document.getElementById("entry-spinner-id-" + this.entry.termOrdinal + this.pathIdentifier);
     spinnerElement?.classList.add("ng-hide");
   }

   showSpinnerContainer() {
   console.log("showSpinnerContainer: entry.paths = " + JSON.stringify(this.entry.paths?.[0].terms));
    let spinnerElementID = "entry-spinner-id-" + this.entry.termOrdinal + this.pathIdentifier;
    console.log("showSpinnerContainer: spinnerElementID = " + spinnerElementID);
    let spinnerElement = document.getElementById(spinnerElementID);
    spinnerElement?.classList.remove("ng-hide");
   }

   showSpinner() {
     this.showSpinnerContainer();
     this.spinnerService.show("entry-spinner");
   }

   hideSpinner() {
     this.spinnerService.hide("entry-spinner");
     this.hideSpinnerContainer();
   }

}
